/**
 * vaul-vue portals its drawer DOM out of `.aia-widget` into document.body.
 * Our variant CSS blocks target those portaled selectors directly, so the
 * variant attribute needs to land on them too.
 *
 * Strategy: keep watching document.body for nodes matching the vaul selectors;
 * when any appears, copy the host's current `data-variant` onto it. Cheap —
 * the observer only fires when DOM mutates near the body, and we touch a node
 * at most once per appearance.
 *
 * Returns a teardown function that disconnects the observer.
 */
const VAUL_SELECTORS = [
    '[data-vaul-drawer-wrapper]',
    '[data-vaul-drawer]',
    '[data-vaul-overlay]',
];

export function startVariantPropagation(host) {
    if (typeof MutationObserver === 'undefined') {
        return () => {};
    }

    const apply = (node) => {
        const variant = host.getAttribute('data-variant');
        if (!variant) return;
        for (const selector of VAUL_SELECTORS) {
            if (node.matches(selector)) {
                node.setAttribute('data-variant', variant);
            }
            node.querySelectorAll(selector).forEach((child) => {
                child.setAttribute('data-variant', variant);
            });
        }
    };

    // Initial one-time sweep via querySelectorAll(document.body) to catch
    // anything already portaled before we started observing.
    apply(document.body);

    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType === 1) apply(node);
            }
        }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
}
